<template>
  <div class="customers-page">
    <!-- ╭──── Top bar: title + “New contact” ╮ -->
    <div class="top-bar">
      <div class="title-block">
        <h1>Contacts</h1>
        <div class="scope-tabs" role="tablist" aria-label="Contact list scope">
          <span class="scope-thumb" :class="contactsScope" aria-hidden="true"></span>
          <button
            type="button"
            role="tab"
            class="scope-tab"
            :class="{ active: contactsScope === 'all' }"
            :aria-selected="contactsScope === 'all'"
            @click="setContactsScope('all')"
          >
            All contacts
          </button>
          <button
            type="button"
            role="tab"
            class="scope-tab"
            :class="{ active: contactsScope === 'mine' }"
            :aria-selected="contactsScope === 'mine'"
            @click="setContactsScope('mine')"
          >
            My contacts
          </button>
        </div>
      </div>
      <div class="top-actions">
        <button class="import-btn" @click="openImportModal">
          <svg class="import-icon" viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M12 4v9m0-9l-4 4m4-4l4 4M4 15v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3"
              fill="none"
              stroke="currentColor"
              stroke-width="1.6"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          Import CSV
        </button>
        <button class="bulk-btn" @click="toggleBulkMode">
          <svg class="bulk-icon" viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M3 11.5V6.2c0-.6.4-1 1-1h5.3c.3 0 .5.1.7.3l9 9a1 1 0 0 1 0 1.4l-4.4 4.4a1 1 0 0 1-1.4 0l-9-9a1 1 0 0 1-.2-.7Z"
              fill="none"
              stroke="currentColor"
              stroke-width="1.6"
              stroke-linejoin="round"
            />
            <circle cx="7.5" cy="8.5" r="1.6" fill="currentColor" />
          </svg>
          {{ bulkMode ? 'Exit bulk tags' : 'Apply tags' }}
        </button>
        <button class="new-btn" @click="showNewModal = true">➕ New contact</button>
      </div>
    </div>

    <!-- Loading spinner / turtle (first paint only) -->
    <p v-if="isLoading && !customers.length" class="loading-message">
      🐢 Whoa dude, there are THOUSANDS of customers! Loading...
    </p>

    <!-- ─────────── Main content ─────────── -->
    <div v-show="!(isLoading && !customers.length)" class="list-panel" :class="{ dimmed: isRefreshing }">
      <p>
        Showing {{ total.toLocaleString() }} {{ contactsScope === 'mine' ? 'assigned to you' : 'total' }} |
        loaded {{ customers.length.toLocaleString() }}
        <span v-if="activeTag" class="tag-filter-msg">
          • filtered by <strong>“{{ activeTag }}”</strong>
          <button class="clear-tag" @click="clearTag">✕</button>
        </span>
        <span v-if="activeFilters.length" class="tag-filter-msg">
          •
          <span v-for="f in activeFilters" :key="f.key" class="filter-chip">
            {{ filterLabel(f.key) }}: <strong>“{{ f.value }}”</strong>
            <button class="clear-tag" @click="clearFilter(f.key)">✕</button>
          </span>
          <button class="clear-tag" @click="clearAllFilters">Clear all</button>
        </span>
      </p>

      <!-- search box + tag‐suggestions -->
      <div class="search-wrapper">
        <input
            v-model="searchRaw"
            placeholder="Search customers…"
            class="search-bar"
            @keydown.enter.prevent="applySuggestionOnEnter"
        />
        <ul v-if="searchMode==='tags' && suggestions.length" class="suggestions">
          <li v-for="t in suggestions" :key="t" @click="selectSuggestion(t)">{{ t }}</li>
        </ul>
      </div>

      <!-- radio toggle -->
      <div class="search-mode">
        <label><input type="radio" value="details" v-model="searchMode" /> By details</label>
        <label><input type="radio" value="tags"    v-model="searchMode" /> By tag</label>
      </div>

      <div v-if="bulkMode" class="bulk-bar">
        <div class="bulk-left">
          <span>{{ selectedIds.length }} selected</span>
          <button class="bulk-select" @click="toggleSelectAll">
            {{ allSelected ? 'Clear all loaded' : 'Select all loaded' }}
          </button>
        </div>
        <div class="bulk-right">
          <div class="bulk-input">
            <input
              v-model="bulkTagInput"
              placeholder="Add tag…"
              @input="fetchBulkTagSuggestions"
              @keydown.enter.prevent="addBulkTagFromInput"
            />
            <button class="bulk-add" @click="addBulkTagFromInput">Add</button>
            <ul v-if="bulkTagSuggestions.length" class="bulk-suggestions">
              <li v-for="t in bulkTagSuggestions" :key="t" @click="addBulkTag(t)">{{ t }}</li>
            </ul>
          </div>
          <div class="bulk-tags" v-if="bulkTags.length">
            <span v-for="(t,i) in bulkTags" :key="t">
              {{ t }} <button @click="removeBulkTag(i)">×</button>
            </span>
          </div>
          <div class="bulk-actions">
            <button class="bulk-cancel" @click="cancelBulkMode">Cancel</button>
            <button class="bulk-apply" @click="applyBulkTags" :disabled="bulkSaving || !canApplyBulk">
              {{ bulkSaving ? 'Applying…' : 'Apply tags' }}
            </button>
          </div>
          <p v-if="bulkError" class="error">{{ bulkError }}</p>
        </div>
      </div>

      <!-- scroll container so we can lazy-load pages -->
      <div class="scroll-box" ref="contactsScroll" @scroll="onScroll">
        <table class="table customers-table">
          <thead>
          <tr>
            <th v-if="bulkMode" class="bulk-col">
              <input type="checkbox" :checked="allSelected" @change="toggleSelectAll" />
            </th>
            <th>★</th>
            <th class="sortable" @click="toggleSort('last_name')">
              Last name
              <span class="sort-indicator" :class="{ active: sortBy==='last_name' }">
                {{ sortBy==='last_name' ? (sortDir==='asc' ? '▲' : '▼') : '▲' }}
              </span>
            </th>
            <th class="sortable" @click="toggleSort('first_name')">
              First name
              <span class="sort-indicator" :class="{ active: sortBy==='first_name' }">
                {{ sortBy==='first_name' ? (sortDir==='asc' ? '▲' : '▼') : '▲' }}
              </span>
            </th>
            <th class="sortable" @click="toggleSort('main_email')">
              Email
              <span class="sort-indicator" :class="{ active: sortBy==='main_email' }">
                {{ sortBy==='main_email' ? (sortDir==='asc' ? '▲' : '▼') : '▲' }}
              </span>
            </th>
            <th>Phone</th>
            <th class="sortable" @click="toggleSort('main_title')">
              Title
              <span class="sort-indicator" :class="{ active: sortBy==='main_title' }">
                {{ sortBy==='main_title' ? (sortDir==='asc' ? '▲' : '▼') : '▲' }}
              </span>
            </th>
            <th class="sortable" @click="toggleSort('company')">
              Company
              <span class="sort-indicator" :class="{ active: sortBy==='company' }">
                {{ sortBy==='company' ? (sortDir==='asc' ? '▲' : '▼') : '▲' }}
              </span>
            </th>
            <th class="sortable" @click="toggleSort('parent_company')">
              Parent Co.
              <span class="sort-indicator" :class="{ active: sortBy==='parent_company' }">
                {{ sortBy==='parent_company' ? (sortDir==='asc' ? '▲' : '▼') : '▲' }}
              </span>
            </th>
            <th class="sortable" @click="toggleSort('chibi_contact')">
              Chibi contact
              <span class="sort-indicator" :class="{ active: sortBy==='chibi_contact' }">
                {{ sortBy==='chibi_contact' ? (sortDir==='asc' ? '▲' : '▼') : '▲' }}
              </span>
            </th>
            <th class="sortable" @click="toggleSort('lead_status')">
              Lead status
              <span class="sort-indicator" :class="{ active: sortBy==='lead_status' }">
                {{ sortBy==='lead_status' ? (sortDir==='asc' ? '▲' : '▼') : '▲' }}
              </span>
            </th>
            <th class="sortable" @click="toggleSort('life_cycle_status')">
              Life Cycle
              <span class="sort-indicator" :class="{ active: sortBy==='life_cycle_status' }">
                {{ sortBy==='life_cycle_status' ? (sortDir==='asc' ? '▲' : '▼') : '▲' }}
              </span>
            </th>
            <th class="sortable" @click="toggleSort('last_emailed_us_at')">
              They emailed Chibi
              <span class="sort-indicator" :class="{ active: sortBy==='last_emailed_us_at' }">
                {{ sortBy==='last_emailed_us_at' ? (sortDir==='asc' ? '▲' : '▼') : '▲' }}
              </span>
            </th>
            <th class="sortable" @click="toggleSort('last_emailed_them_at')">
              Chibi emailed them
              <span class="sort-indicator" :class="{ active: sortBy==='last_emailed_them_at' }">
                {{ sortBy==='last_emailed_them_at' ? (sortDir==='asc' ? '▲' : '▼') : '▲' }}
              </span>
            </th>
            <th>Newsletter</th><th>Lists</th><th>Tags</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="c in customers" :key="c.id">
            <td v-if="bulkMode" class="bulk-col">
              <input
                type="checkbox"
                :checked="isSelected(c.id)"
                @change="toggleSelect(c.id)"
              />
            </td>
            <td>
              <button
                type="button"
                class="star-btn"
                :title="c.starred ? 'Click to unstar' : 'Click to star'"
                @click="toggleContactStar(c)"
                aria-label="Star contact"
              >
                {{ c.starred ? '★' : '☆' }}
              </button>
            </td>
            <td>
              <router-link
                  :to="`/customers/${c.id}`"
                  class="truncate"
                  :title="contactDisplayName(c)"
              >
                {{ namePart(c.last_name) }}
              </router-link>
            </td>
            <td>
              <router-link
                  :to="`/customers/${c.id}`"
                  class="truncate"
                  :title="contactDisplayName(c)"
              >
                {{ namePart(c.first_name) }}
              </router-link>
            </td>
            <td><span class="truncate" :title="c.main_email">{{ c.main_email }}</span></td>
            <td>
                <span class="truncate" :title="(c.phone_numbers||[]).join(', ')">
                  {{ (c.phone_numbers||[]).join(', ') || '—' }}
                </span>
            </td>
            <td><span class="truncate" :title="c.main_title">{{ c.main_title || '—' }}</span></td>
            <td>
              <button
                v-if="c.company"
                class="value-link truncate"
                :title="c.company"
                @click="applyFilter('company', c.company)"
              >
                {{ c.company }}
              </button>
              <span v-else class="truncate">—</span>
            </td>
            <td>
              <button
                v-if="c.parent_company"
                class="value-link truncate"
                :title="c.parent_company"
                @click="applyFilter('parent_company', c.parent_company)"
              >
                {{ c.parent_company }}
              </button>
              <span v-else class="truncate">—</span>
            </td>
            <td><span class="truncate" :title="c.chibi_contact || ''">{{ c.chibi_contact || '—' }}</span></td>
            <td>
              <button
                v-if="c.lead_status"
                class="value-link truncate"
                :title="c.lead_status"
                @click="applyFilter('lead_status', c.lead_status)"
              >
                {{ leadStatusLabel(c.lead_status) }}
              </button>
              <span v-else class="truncate">—</span>
            </td>
            <td>
              <button
                v-if="c.life_cycle_status"
                class="value-link truncate"
                :title="c.life_cycle_status"
                @click="applyFilter('life_cycle_status', c.life_cycle_status)"
              >
                {{ lifeCycleLabel(c.life_cycle_status) }}
              </button>
              <span v-else class="truncate">—</span>
            </td>
            <td><span class="truncate" :title="displayLastEmailedUs(c)">{{ displayLastEmailedUs(c) }}</span></td>
            <td><span class="truncate" :title="displayLastEmailedThem(c)">{{ displayLastEmailedThem(c) }}</span></td>
            <td>
              <button
                v-if="!c.mailchimp_lists.length"
                class="value-link"
                @click="applyFilter('newsletter_status', 'not_yet_subscribed')"
              >
                Not yet subscribed
              </button>
              <button
                v-else-if="c.newsletter_subscriber"
                class="value-link"
                @click="applyFilter('newsletter_status', 'subscribed')"
              >
                Subscribed
              </button>
              <button
                v-else
                class="value-link"
                @click="applyFilter('newsletter_status', 'unsubscribed')"
              >
                Unsubscribed
              </button>
            </td>
            <td>
              <button
                v-for="l in c.mailchimp_lists"
                :key="l"
                class="pill value-pill"
                :title="l"
                @click="applyFilter('mailchimp_list', l)"
              >
                {{ l }}
              </button>
              <span v-if="!c.mailchimp_lists.length">—</span>
            </td>
            <td>
                <span
                    v-for="t in c.tags"
                    :key="t"
                    class="pill tag-pill"
                    :class="{ active:t===activeTag }"
                    @click="filterByTag(t)"
                    :title="t"
                >{{ t }}</span>
            </td>
          </tr>
          </tbody>
        </table>

        <div v-if="loadingMore && !isRefreshing" class="loading-message">Loading more…</div>
      </div>
    </div>

    <NewCustomerModal
        v-if="showNewModal"
        @close="showNewModal=false"
        @created="handleCreated"
    />

    <div v-if="showImportModal" class="overlay">
      <div class="modal">
        <h2>Import contacts (CSV)</h2>

        <div
          class="drop-zone"
          :class="{ dragover: importDragOver }"
          @dragover.prevent="onImportDragOver"
          @dragleave.prevent="onImportDragLeave"
          @drop.prevent="onImportDrop"
        >
          <p v-if="!importFile">
            Drag & drop a CSV here, or
            <span class="browse-link" @click="openFilePicker">browse</span>
          </p>
          <p v-else>Selected: {{ importFile.name }}</p>
          <input
            type="file"
            ref="importFileInput"
            accept=".csv,text/csv"
            @change="onImportFileChange"
            hidden
          />
        </div>

        <div v-if="importFile" class="file-actions">
          <button @click="clearImportFile" :disabled="importLoading || importImporting">Remove</button>
          <button @click="previewImport" :disabled="importLoading || importImporting">
            {{ importLoading ? 'Previewing…' : 'Preview' }}
          </button>
        </div>

        <p v-if="importError" class="error">{{ importError }}</p>

        <div class="import-help">
          <button class="help-toggle" type="button" @click="toggleImportHelp">
            <svg class="help-icon" viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="1.6" />
              <path
                d="M9.5 9.2a2.6 2.6 0 0 1 5 1c0 1.6-1.6 2-2.2 2.7-.3.4-.3.8-.3 1.6"
                fill="none"
                stroke="currentColor"
                stroke-width="1.6"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <circle cx="12" cy="17.2" r="1" fill="currentColor" />
            </svg>
            <span>What does the CSV expect?</span>
          </button>
          <a class="download-link" href="/api/customers/import/template" download>
            Click here to download sample CSV
          </a>
        </div>

        <div v-if="showImportHelp" class="help-panel">
          <p>Headers should match these fields (order does not matter):</p>
          <p class="help-fields">
            last_contact_at, first_name, last_name, main_email, main_title, company, parent_company,
            address_line1, address_line2, city, state, postal_code, country, tag, tags, notes,
            lead_status, life_cycle_status, referral_source, age_group, subjects_taught,
            newsletter_subscriber (true/false)
          </p>
          <p>
            Dates can be ISO‑8601 (YYYY‑MM‑DD or full timestamp). Address parts are combined into a
            single billing address internally. Tags in <em>tag</em> or <em>tags</em> are merged.
          </p>
        </div>

        <div v-if="importPreview" class="preview">
          <p class="summary">
            {{ importPreview.valid_rows }} ready ·
            {{ importPreview.new_count }} new ·
            {{ importPreview.update_count }} update ·
            {{ importPreview.invalid_rows }} skipped
          </p>

          <div class="preview-table">
            <table>
              <thead>
                <tr>
                  <th>Row</th>
                  <th>Status</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Company</th>
                  <th>Tags</th>
                  <th>Issues</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in importPreview.rows"
                  :key="`${row.row_number}-${row.main_email || 'missing'}`"
                  :class="{ invalid: row.errors.length }"
                >
                  <td>{{ row.row_number }}</td>
                  <td>
                    <span class="status" :class="row.status">
                      {{ row.status }}
                    </span>
                  </td>
                  <td>{{ [row.first_name, row.last_name].filter(Boolean).join(' ') || '—' }}</td>
                  <td>{{ row.main_email || '—' }}</td>
                  <td>{{ row.company || '—' }}</td>
                  <td>
                    <span v-if="row.tags && row.tags.length">{{ row.tags.join(', ') }}</span>
                    <span v-else>—</span>
                  </td>
                  <td>
                    <span v-if="row.errors.length" class="error-inline">
                      {{ row.errors.join('; ') }}
                    </span>
                    <span v-else>—</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="actions">
          <button @click="closeImportModal" :disabled="importLoading || importImporting">Cancel</button>
          <button
            v-if="importPreview"
            @click="confirmImport"
            :disabled="importImporting || importPreview.valid_rows === 0"
          >
            {{ importImporting ? 'Importing…' : `Import ${importPreview.valid_rows}` }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CustomersService  from '@/services/CustomersService';
import TagsService       from '@/services/TagsService';
import { debounce }      from 'lodash-es';
import NewCustomerModal  from '@/components/NewCustomerModal.vue';
import { lifeCycleLabel, leadStatusLabel } from '@/data/contactStatuses';

export default {
  name: 'CustomersPage',
  components: { NewCustomerModal },

  data() {
    return {
      customers:   [],
      page:        1,
      perPage:     100,
      total:       0,
      isLoading:   true,
      loadingMore: false,
      searchRaw:   '',
      searchQuery: '',
      searchMode:  'details',
      suggestions: [],
      activeTag:   null,
      sortBy: 'last_name',
      sortDir: 'asc',
      filters: {
        company: null,
        parent_company: null,
        lead_status: null,
        life_cycle_status: null,
        mailchimp_list: null,
        newsletter_status: null
      },
      showNewModal:false,
      showImportModal: false,
      importFile: null,
      importPreview: null,
      importError: '',
      importLoading: false,
      importImporting: false,
      importDragOver: false,
      showImportHelp: false,
      lastContactCache: {},
      lastContactLoading: {},
      bulkMode: false,
      selectedIds: [],
      bulkTagInput: '',
      bulkTagSuggestions: [],
      bulkTags: [],
      bulkSaving: false,
      bulkError: '',
      contactsScope: localStorage.getItem('contactsScope') === 'mine' ? 'mine' : 'all',
      isRefreshing: false,
      applyingQuery: false,
      contactsScrollTop: 0
    };
  },

  computed: {
    allSelected() {
      return this.customers.length > 0 && this.selectedIds.length === this.customers.length;
    },
    canApplyBulk() {
      return this.selectedIds.length > 0 && this.bulkTags.length > 0;
    },
    activeFilters() {
      return Object.entries(this.filters)
        .filter(([, v]) => v)
        .map(([k, v]) => ({ key: k, value: v }));
    }
  },

  created() {
    this.hydrateFromQuery(this.$route.query)
    this.$watch(
        'searchRaw',
        debounce(async v => {
          if (this.applyingQuery) return
          this.searchQuery = v.toLowerCase().trim();
          if (this.searchMode==='tags' && this.searchQuery) {
            try {
              const { data } = await TagsService.search(this.searchQuery);
              this.suggestions = data.slice(0,10);
            } catch {
              this.suggestions = [];
            }
          } else {
            this.suggestions = [];
          }
          this.resetAndLoad();
          this.syncQuery();
        }, 300)
    );

    this.$watch('searchMode', () => {
      if (this.applyingQuery) return
      this.resetAndLoad();
      this.syncQuery();
    });
    this.$watch('activeTag',  () => {
      if (this.applyingQuery) return
      this.resetAndLoad();
      this.syncQuery();
    });

    this.resetAndLoad();
  },
  activated() {
    this.$nextTick(() => this.restoreScroll())
  },
  watch: {
    '$route.query'(to) {
      if (this.$route.name !== 'Contacts') return
      if (this.applyingQuery) return
      this.hydrateFromQuery(to, { loadIfChanged: true })
    }
  },

  methods: {
    lifeCycleLabel,
    leadStatusLabel,
    contactsQueryFromState() {
      const q = {}
      if (this.searchRaw) q.q = this.searchRaw
      if (this.searchMode === 'tags') q.mode = 'tags'
      if (this.activeTag) q.tag = this.activeTag
      if (this.sortBy && this.sortBy !== 'last_name') q.sort = this.sortBy
      if (this.sortDir === 'desc') q.dir = 'desc'
      if (this.contactsScope === 'mine') q.scope = 'mine'
      Object.entries(this.filters).forEach(([k, v]) => {
        if (v) q[k] = v
      })
      return q
    },
    queryEqualsState(query) {
      const a = this.contactsQueryFromState()
      const keys = new Set([...Object.keys(a), ...Object.keys(query || {})])
      for (const k of keys) {
        if (k === 'id') continue
        if (String(a[k] || '') !== String((query || {})[k] || '')) return false
      }
      return true
    },
    hydrateFromQuery(query, { loadIfChanged } = {}) {
      const q = query || {}
      const nextRaw = q.q ? String(q.q) : ''
      const nextMode = q.mode === 'tags' ? 'tags' : 'details'
      const nextTag = q.tag ? String(q.tag) : null
      const nextSort = q.sort ? String(q.sort) : 'last_name'
      const nextDir = q.dir === 'desc' ? 'desc' : 'asc'
      const nextScope = q.scope === 'mine' ? 'mine' : (q.scope === 'all' ? 'all' : this.contactsScope)
      const nextFilters = {
        company: q.company ? String(q.company) : null,
        parent_company: q.parent_company ? String(q.parent_company) : null,
        lead_status: q.lead_status ? String(q.lead_status) : null,
        life_cycle_status: q.life_cycle_status ? String(q.life_cycle_status) : null,
        mailchimp_list: q.mailchimp_list ? String(q.mailchimp_list) : null,
        newsletter_status: q.newsletter_status ? String(q.newsletter_status) : null
      }
      const changed =
        nextRaw !== this.searchRaw ||
        nextMode !== this.searchMode ||
        nextTag !== this.activeTag ||
        nextSort !== this.sortBy ||
        nextDir !== this.sortDir ||
        nextScope !== this.contactsScope ||
        JSON.stringify(nextFilters) !== JSON.stringify(this.filters)

      this.applyingQuery = true
      this.searchRaw = nextRaw
      this.searchQuery = nextRaw.toLowerCase().trim()
      this.searchMode = nextMode
      this.activeTag = nextTag
      this.sortBy = nextSort
      this.sortDir = nextDir
      this.contactsScope = nextScope
      this.filters = nextFilters
      this.applyingQuery = false

      if (loadIfChanged && changed) this.resetAndLoad()
    },
    syncQuery() {
      if (this.$route.name !== 'Contacts') return
      const q = this.contactsQueryFromState()
      if (this.queryEqualsState(this.$route.query)) return
      this.applyingQuery = true
      this.$router.replace({ query: q }).catch(() => {}).finally(() => {
        this.applyingQuery = false
      })
    },
    restoreScroll() {
      const el = this.$refs.contactsScroll
      if (el && typeof this.contactsScrollTop === 'number') {
        el.scrollTop = this.contactsScrollTop
      }
    },
    setContactsScope(scope) {
      if (this.contactsScope === scope) return
      this.contactsScope = scope
      localStorage.setItem('contactsScope', scope)
      this.isRefreshing = true
      this.resetAndLoad()
      this.syncQuery()
    },
    async loadPage(reset = false) {
      if (reset) {
        this.page = 1;
      }
      this.loadingMore = true;
      try {
        const { data } = await CustomersService.list({
          page:      this.page,
          per_page:  this.perPage,
          q:         this.searchMode==='details' ? this.searchQuery : undefined,
          tag:       this.activeTag || (this.searchMode==='tags' ? this.searchQuery : undefined),
          sort_by:   this.sortBy,
          sort_dir:  this.sortDir,
          company:   this.filters.company || undefined,
          parent_company: this.filters.parent_company || undefined,
          lead_status: this.filters.lead_status || undefined,
          life_cycle_status: this.filters.life_cycle_status || undefined,
          mailchimp_list: this.filters.mailchimp_list || undefined,
          newsletter_status: this.filters.newsletter_status || undefined,
          assigned: this.contactsScope === 'mine' ? 'me' : undefined
        });
        this.total = data.total;
        this.customers = reset ? data.customers : [...this.customers, ...data.customers];
        this.fetchLastContactsFor(this.customers);
        this.page++;
      } catch (err) {
        console.error('load customers failed', err);
      } finally {
        this.isLoading   = false;
        this.loadingMore = false;
        this.isRefreshing = false;
      }
    },
    displayLastEmailedUs(c) {
      return c.last_emailed_us_at || this.lastContactCache[c.id]?.us || '—';
    },
    displayLastEmailedThem(c) {
      return c.last_emailed_them_at || this.lastContactCache[c.id]?.them || '—';
    },
    async fetchLastContactsFor(list) {
      for (const c of list) {
        if (!c || !c.id || !c.main_email) continue;
        if (c.last_emailed_us_at && c.last_emailed_them_at) continue;
        if (Object.prototype.hasOwnProperty.call(this.lastContactCache, c.id)) continue;
        if (this.lastContactLoading[c.id]) continue;
        this.lastContactLoading[c.id] = true;
        try {
          const { data } = await CustomersService.lastEmail(c.id, { shallow: true });
          const us = data && data.last_emailed_us_at;
          const them = data && data.last_emailed_them_at;
          if (us) c.last_emailed_us_at = us;
          if (them) c.last_emailed_them_at = them;
          if (data && data.last_email_at) c.last_contact_at = data.last_email_at;
          this.lastContactCache[c.id] = { us: us || null, them: them || null };
        } catch {
          this.lastContactCache[c.id] = { us: null, them: null };
        } finally {
          delete this.lastContactLoading[c.id];
        }
      }
    },
    resetAndLoad() {
      if (this.bulkMode) this.selectedIds = [];
      if (this.customers.length) this.isRefreshing = true;
      this.loadPage(true);
    },
    contactDisplayName(c) {
      return [c.first_name, c.last_name].filter(Boolean).join(' ').trim() || '—';
    },
    namePart(value) {
      return value || '—';
    },
    toggleContactStar(c) {
      CustomersService.toggleStar(c.id)
        .then(({ data }) => {
          c.starred = data.starred;
          c.starred_by_me = data.starred_by_me;
          c.starred_by = data.starred_by;
        })
        .catch(() => alert('Could not update star.'));
    },
    onScroll(e) {
      const el = e.target;
      this.contactsScrollTop = el.scrollTop
      if (!this.loadingMore && this.customers.length < this.total &&
          el.scrollTop + el.clientHeight >= el.scrollHeight - 200) {
        this.loadPage();
      }
    },
    filterByTag(t) { this.activeTag = this.activeTag===t ? null : t },
    clearTag()      { this.activeTag = null },
    applyFilter(key, value) {
      this.filters = { ...this.filters, [key]: value };
      this.resetAndLoad();
      this.syncQuery();
    },
    clearFilter(key) {
      this.filters = { ...this.filters, [key]: null };
      this.resetAndLoad();
      this.syncQuery();
    },
    clearAllFilters() {
      this.filters = {
        company: null,
        parent_company: null,
        lead_status: null,
        life_cycle_status: null,
        mailchimp_list: null,
        newsletter_status: null
      };
      this.resetAndLoad();
      this.syncQuery();
    },
    filterLabel(key) {
      switch (key) {
        case 'company': return 'Company';
        case 'parent_company': return 'Parent';
        case 'lead_status': return 'Lead status';
        case 'life_cycle_status': return 'Life cycle';
        case 'mailchimp_list': return 'List';
        case 'newsletter_status': return 'Newsletter';
        default: return key;
      }
    },
    selectSuggestion(t) { this.searchRaw = t; this.suggestions = [] },
    applySuggestionOnEnter() {
      if (this.searchMode==='tags' && this.suggestions.length===1) {
        this.selectSuggestion(this.suggestions[0]);
      }
    },
    toggleSort(column) {
      if (this.sortBy === column) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortBy = column;
        this.sortDir = 'asc';
      }
      this.resetAndLoad();
      this.syncQuery();
    },
    handleCreated(id) {
      this.showNewModal = false;
      this.$router.push(`/customers/${id}`);
    },
    toggleBulkMode() {
      if (this.bulkMode) {
        this.cancelBulkMode();
      } else {
        this.bulkMode = true;
      }
    },
    cancelBulkMode() {
      this.bulkMode = false;
      this.resetBulkState();
    },
    resetBulkState() {
      this.selectedIds = [];
      this.bulkTagInput = '';
      this.bulkTagSuggestions = [];
      this.bulkTags = [];
      this.bulkSaving = false;
      this.bulkError = '';
    },
    isSelected(id) {
      return this.selectedIds.includes(id);
    },
    toggleSelect(id) {
      if (this.isSelected(id)) {
        this.selectedIds = this.selectedIds.filter(v => v !== id);
      } else {
        this.selectedIds = [...this.selectedIds, id];
      }
    },
    toggleSelectAll() {
      if (this.allSelected) {
        this.selectedIds = [];
      } else {
        this.selectedIds = this.customers.map(c => c.id);
      }
    },
    fetchBulkTagSuggestions() {
      const q = this.bulkTagInput.trim();
      if (!q) return (this.bulkTagSuggestions = []);
      TagsService.search(q)
        .then(({ data }) => {
          this.bulkTagSuggestions = data
            .filter(t => !this.bulkTags.includes(t))
            .slice(0, 10);
        })
        .catch(() => (this.bulkTagSuggestions = []));
    },
    addBulkTag(tag) {
      if (!tag) return;
      if (!this.bulkTags.includes(tag)) this.bulkTags.push(tag);
      this.bulkTagInput = '';
      this.bulkTagSuggestions = [];
    },
    addBulkTagFromInput() {
      const val = this.bulkTagInput.trim();
      if (val) this.addBulkTag(val);
    },
    removeBulkTag(i) {
      this.bulkTags.splice(i, 1);
    },
    async applyBulkTags() {
      if (!this.canApplyBulk) {
        this.bulkError = 'Select contacts and at least one tag.';
        return;
      }
      this.bulkSaving = true;
      this.bulkError = '';
      try {
        await CustomersService.bulkApplyTags({
          customer_ids: this.selectedIds,
          tags: this.bulkTags
        });
        this.cancelBulkMode();
        this.resetAndLoad();
      } catch (err) {
        console.error('Bulk tag apply failed', err);
        this.bulkError = 'Could not apply tags. Please try again.';
      } finally {
        this.bulkSaving = false;
      }
    },
    openImportModal() {
      this.resetImportState();
      this.showImportModal = true;
    },
    closeImportModal() {
      this.showImportModal = false;
      this.resetImportState();
    },
    resetImportState() {
      this.importFile = null;
      this.importPreview = null;
      this.importError = '';
      this.importLoading = false;
      this.importImporting = false;
      this.importDragOver = false;
      this.showImportHelp = false;
    },
    toggleImportHelp() {
      this.showImportHelp = !this.showImportHelp;
    },
    openFilePicker() {
      if (this.$refs.importFileInput) {
        this.$refs.importFileInput.click();
      }
    },
    onImportDragOver() { this.importDragOver = true },
    onImportDragLeave() { this.importDragOver = false },
    onImportDrop(e) {
      this.importDragOver = false;
      this.selectImportFile(e.dataTransfer.files[0]);
    },
    onImportFileChange(e) {
      this.selectImportFile(e.target.files[0]);
    },
    selectImportFile(file) {
      if (!file) return;
      this.importError = '';
      this.importPreview = null;
      this.importFile = file;
    },
    clearImportFile() {
      this.importFile = null;
      this.importPreview = null;
      this.importError = '';
    },
    async previewImport() {
      if (!this.importFile) return;
      this.importError = '';
      this.importLoading = true;
      try {
        const { data } = await CustomersService.previewImport(this.importFile);
        this.importPreview = data;
      } catch (err) {
        console.error('CSV preview failed', err);
        this.importError = 'Could not preview CSV. Check format and try again.';
      } finally {
        this.importLoading = false;
      }
    },
    async confirmImport() {
      if (!this.importFile || !this.importPreview) return;
      this.importImporting = true;
      this.importError = '';
      try {
        const { data } = await CustomersService.importCsv(this.importFile);
        alert(`Import complete: ${data.created} created, ${data.updated} updated, ${data.skipped} skipped.`);
        this.closeImportModal();
        this.resetAndLoad();
      } catch (err) {
        console.error('CSV import failed', err);
        this.importError = 'Import failed. Please try again.';
      } finally {
        this.importImporting = false;
      }
    }
  }
};
</script>

<style scoped>
/* ── Layout / top bar ─────────────────────────────────────────── */
.top-bar { display:flex; justify-content:space-between; align-items:flex-end; gap:1rem; flex-wrap:wrap; margin-bottom:.75rem }
.title-block { display:flex; flex-direction:column; gap:.55rem }
.title-block h1 { margin:0 }
.top-actions { display:flex; gap:.5rem; align-items:center }
.scope-tabs {
  position: relative;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
  padding: 0.2rem;
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.28);
}
.scope-thumb {
  position: absolute;
  top: 0.2rem;
  left: 0.2rem;
  width: calc((100% - 0.4rem) / 2);
  height: calc(100% - 0.4rem);
  border-radius: 7px;
  background: linear-gradient(135deg, rgba(26, 188, 156, 0.32), rgba(251, 211, 141, 0.16));
  box-shadow: inset 0 0 0 1px rgba(167, 243, 208, 0.22);
  transition: transform 0.32s cubic-bezier(0.22, 1, 0.36, 1);
  pointer-events: none;
}
.scope-thumb.all { transform: translateX(0); }
.scope-thumb.mine { transform: translateX(100%); }
.scope-tab {
  position: relative;
  z-index: 1;
  border: none;
  background: transparent;
  color: rgba(255, 255, 255, 0.58);
  padding: 0.48rem 1.05rem;
  border-radius: 7px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.9rem;
  transition: color 0.22s ease;
}
.scope-tab:hover { color: #fff; }
.scope-tab.active {
  color: #ecfffa;
}
.scope-tab:focus-visible {
  outline: 2px solid rgba(167, 243, 208, 0.7);
  outline-offset: 2px;
}
.list-panel {
  transition: opacity 0.28s ease, filter 0.28s ease;
}
.list-panel.dimmed {
  opacity: 0.45;
  filter: saturate(0.85);
  pointer-events: none;
}
.new-btn { background:#38a169; border:none; border-radius:5px; padding:.4rem .9rem; color:#fff; font-weight:700; cursor:pointer }
.new-btn:hover { background:#2f855a }
.import-btn {
  background:#3182ce;
  border:none;
  border-radius:5px;
  padding:.4rem .9rem;
  color:#fff;
  font-weight:700;
  cursor:pointer;
  display:flex;
  align-items:center;
  gap:.4rem;
}
.import-btn:hover { background:#2b6cb0 }
.import-icon {
  width:16px;
  height:16px;
  color:#dbeafe;
  filter: drop-shadow(0 0 6px rgba(99,179,237,.35));
}
.bulk-btn {
  background:#805ad5;
  border:none;
  border-radius:5px;
  padding:.4rem .9rem;
  color:#fff;
  font-weight:700;
  cursor:pointer;
  display:flex;
  align-items:center;
  gap:.4rem;
}
.bulk-btn:hover { background:#6b46c1 }
.bulk-icon {
  width:16px;
  height:16px;
  color:#f2e9ff;
  filter: drop-shadow(0 0 6px rgba(128,90,213,.35));
}

/* ── Search & suggestions ─────────────────────────────────────── */
.search-wrapper { position:relative; max-width:400px }
.search-bar { width:100%; padding:12px 20px; margin-bottom:.5rem; font-size:1rem; background:transparent; color:#fff; border:1px solid #fff; border-radius:5px }
.suggestions { position:absolute; left:0; right:0; top:100%; background:#111; border:1px solid #444; border-top:none; max-height:180px; overflow-y:auto; list-style:none; margin:0; padding:0; z-index:20 }
.suggestions li { padding:6px 10px; cursor:pointer; color:#ddd }
.suggestions li:hover { background:#333; color:#fff }

/* radio row */
.search-mode { display:flex; gap:1.5rem; margin:.5rem 0 1rem; font-size:.9rem }
.search-mode input { margin-right:4px }

/* ── Scroll box: allow both vertical & horizontal ───────────────── */
.scroll-box {
  height: 70vh;
  overflow-y: auto;
  overflow-x: auto;
  border: 1px solid rgba(255,255,255,.12);
  border-radius: 4px;
  padding: 4px;
}

/* ── Table (auto layout) ─────────────────────────────────────── */
.customers-table {
  border-collapse: collapse;
  table-layout: auto;
  min-width: 100%;
}
.customers-table th {
  text-align:left; padding-bottom:.4rem; border-bottom:2px solid var(--pink); color:#fff
}
.customers-table th.sortable { cursor:pointer; user-select:none }
.sort-indicator {
  font-size:.7rem;
  margin-left:.25rem;
  color:rgba(255,255,255,.35);
}
.sort-indicator.active { color:#fff }
.customers-table td {
  padding:4px 8px; border-bottom:1px solid rgba(255,255,255,.12); color:#fff
}
.star-btn {
  background: none;
  border: none;
  color: #f6e05e;
  cursor: pointer;
  font-size: 1.1rem;
  padding: 0;
  line-height: 1;
}
.star-btn:hover { color: #fbd38d; }

/* optional ellipsis helper */
.truncate {
  max-width:100%; overflow:hidden; text-overflow:ellipsis; white-space:nowrap
}

/* ── pills ───────────────────────────────────────────────────── */
.pill { display:inline-block; padding:2px 8px; margin:0 4px 4px 0; border-radius:999px; background:rgba(255,255,255,.15); color:#fff; font-size:.85em; border:1px solid rgba(255,255,255,.25) }
.value-pill { cursor:pointer; }
.value-pill:hover { background:rgba(255,255,255,.3); }
.value-link {
  background:none;
  border:none;
  color:#fbd38d;
  cursor:pointer;
  padding:0;
  text-decoration:underline;
  font: inherit;
}
.value-link:hover { color:#fff; }
.filter-chip { margin-left:.4rem; }
.tag-pill { cursor:pointer; transition:background .2s,color .2s }
.tag-pill:hover, .tag-pill.active { background:var(--pink); color:#fff }

/* misc */
.loading-message { font-size:1.1rem; font-style:italic; color:#ffa; margin:1rem 0 }
.tag-filter-msg  { font-size:.9em; color:var(--pink) }
.clear-tag       { background:none; border:none; color:inherit; cursor:pointer; margin-left:.25rem }

/* ── Bulk tag bar ─────────────────────────────────────────────── */
.bulk-bar {
  display:flex;
  flex-wrap:wrap;
  gap:1rem;
  align-items:flex-start;
  background: rgba(255,255,255,0.04);
  border:1px solid rgba(255,255,255,0.12);
  border-radius:6px;
  padding:.75rem;
  margin: .5rem 0 1rem;
}
.bulk-left {
  display:flex;
  align-items:center;
  gap:.75rem;
  font-size:.9rem;
}
.bulk-select {
  background:none;
  border:none;
  color:#c3dafe;
  cursor:pointer;
  text-decoration:underline;
  padding:0;
}
.bulk-right { flex:1; min-width:260px; }
.bulk-input { position:relative; display:flex; gap:.5rem; align-items:center; }
.bulk-input input {
  flex:1;
  padding:.4rem .6rem;
  border-radius:4px;
  border:1px solid #555;
  background:#2d3040;
  color:#fff;
}
.bulk-add {
  background:#3182ce;
  border:none;
  border-radius:4px;
  padding:.35rem .7rem;
  color:#fff;
  font-weight:600;
}
.bulk-suggestions {
  position:absolute;
  left:0;
  right:0;
  top:100%;
  background:#111;
  border:1px solid #444;
  border-top:none;
  max-height:180px;
  overflow-y:auto;
  list-style:none;
  margin:0;
  padding:0;
  z-index:20;
}
.bulk-suggestions li { padding:6px 10px; cursor:pointer; color:#ddd }
.bulk-suggestions li:hover { background:#333; color:#fff }
.bulk-tags { margin-top:.5rem; }
.bulk-tags span {
  display:inline-block;
  background:#4a5568;
  border-radius:4px;
  padding:.15rem .4rem;
  margin-right:.25rem;
  font-size:.8rem;
}
.bulk-tags button {
  background:none;
  border:none;
  color:#fed7d7;
  margin-left:4px;
  cursor:pointer;
}
.bulk-actions {
  display:flex;
  gap:.75rem;
  margin-top:.5rem;
  align-items:center;
}
.bulk-cancel {
  background:#4a5568;
  border:none;
  border-radius:4px;
  padding:.35rem .7rem;
  color:#fff;
}
.bulk-apply {
  background:#38a169;
  border:none;
  border-radius:4px;
  padding:.35rem .7rem;
  color:#fff;
  font-weight:700;
}
.bulk-col { width:32px; text-align:center; }

/* ── Import modal ─────────────────────────────────────────────────── */
.overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 50;
}
.modal {
  width: 100%;
  max-width: 900px;
  background: #1e1e2f;
  border-radius: 8px;
  padding: 1.5rem;
  color: #fff;
  box-shadow: 0 8px 24px rgba(0,0,0,.4);
  max-height: 90vh;
  overflow: hidden;
}
.drop-zone {
  margin: 0 0 .75rem;
  padding: 1rem;
  text-align: center;
  border: 2px dashed #888;
  border-radius: 6px;
  transition: background .2s;
}
.drop-zone.dragover { background: rgba(255,255,255,.08) }
.browse-link { color:#63b3ed; text-decoration:underline; cursor:pointer }
.file-actions {
  display:flex; justify-content:flex-end; gap:.75rem; margin-bottom:.5rem
}
.file-actions button {
  padding:.35rem .7rem; border:none; border-radius:4px; cursor:pointer; font-weight:600
}
.file-actions button:last-child { background:#3182ce; color:#fff }
.summary { margin:.5rem 0; color:#fbd38d }
.preview { margin-top:.75rem }
.preview-table {
  border:1px solid rgba(255,255,255,.12);
  border-radius:4px;
  overflow:auto;
  max-height:45vh;
}
.preview-table table { width:100%; border-collapse:collapse; font-size:.85rem }
.preview-table th, .preview-table td {
  text-align:left; padding:6px 8px; border-bottom:1px solid rgba(255,255,255,.12)
}
.preview-table thead th { position:sticky; top:0; background:#2d3040 }
.preview-table tr.invalid { background: rgba(229,62,62,0.12) }
.status {
  display:inline-block; padding:2px 6px; border-radius:999px; font-size:.75rem; text-transform:uppercase
}
.status.new { background: rgba(72,187,120,0.25); color:#9ae6b4 }
.status.update { background: rgba(99,179,237,0.25); color:#90cdf4 }
.error-inline { color:#feb2b2 }
.import-help {
  display:flex;
  align-items:center;
  gap:1rem;
  flex-wrap:wrap;
  margin:.5rem 0 0;
}
.help-toggle,
.download-link {
  background:none;
  border:none;
  color:#c3dafe;
  cursor:pointer;
  text-decoration:underline;
  padding:0;
  font-size:.9rem;
  display:inline-flex;
  align-items:center;
  gap:.35rem;
}
.help-toggle:hover,
.download-link:hover { color:#fff }
.help-icon {
  width:16px;
  height:16px;
  color:#d6bcfa;
  filter: drop-shadow(0 0 6px rgba(214,188,250,.35));
}
.help-panel {
  margin-top:.6rem;
  padding:.75rem;
  border:1px solid rgba(255,255,255,.12);
  border-radius:6px;
  background: rgba(255,255,255,.04);
  font-size:.88rem;
  color:#e2e8f0;
}
.help-panel p { margin:.35rem 0 }
.help-fields { color:#fbd38d }
.actions {
  display:flex; justify-content:flex-end; gap:.75rem; margin-top:1rem
}
.actions button {
  padding:.4rem .9rem; border:none; border-radius:4px; cursor:pointer; font-weight:bold
}
.actions button:first-child { background:#4a5568; color:#fff }
.actions button:last-child { background:#38a169; color:#fff }
.error { color:#feb2b2; margin-top:.5rem }
</style>
